import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Calendar, User, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import { colors, typography } from '../../styles/theme';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticle();
    loadRelatedArticles();
  }, [slug]);

  async function loadArticle() {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.CONTENT.ARTICLE(slug));
      setArticle(response.article);
    } catch (err) {
      console.error('Failed to load article:', err);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  }

  async function loadRelatedArticles() {
    try {
      const response = await api.get(API_ENDPOINTS.CONTENT.ARTICLES);
      // Filter out current article and limit to 3
      const related = (response.articles || [])
        .filter(a => a.slug !== slug)
        .slice(0, 3);
      setRelatedArticles(related);
    } catch (err) {
      console.error('Failed to load related articles:', err);
    }
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: colors.textPrimary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Article Not Found</h1>
        <p style={{ color: colors.textSecondary, marginBottom: '2rem' }}>
          The article you're looking for doesn't exist.
        </p>
        <Link
          to="/articles"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: colors.primary,
            color: colors.textPrimary,
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600'
          }}
        >
          <ArrowLeft size={18} />
          Back to Articles
        </Link>
      </div>
    );
  }

  // Prepare structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "datePublished": article.date,
    "author": {
      "@type": "Person",
      "name": article.author || "IQ Reps"
    },
    "publisher": {
      "@type": "Organization",
      "name": "IQ Reps",
      "logo": {
        "@type": "ImageObject",
        "url": "https://multinback.com/logo.png"
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: colors.textPrimary,
      fontFamily: '"DM Sans", system-ui, sans-serif'
    }}>
      {/* SEO Meta Tags */}
      <SEO
        title={`${article.title} | IQ Reps`}
        description={article.description}
        keywords={article.tags?.join(', ') || 'n-back training, cognitive training'}
        canonicalUrl={`/articles/${article.slug}`}
        ogType="article"
      />

      {/* JSON-LD Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Public Navigation */}
      <nav style={{
        padding: '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>
        <div 
          onClick={() => navigate('/')}
          style={{
            fontSize: '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            cursor: 'pointer'
          }}>
          IQ Reps
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/articles')}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.primary,
              fontSize: '1rem',
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              fontWeight: '600'
            }}
          >
            Articles
          </button>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.textTertiary,
              fontSize: '1rem',
              cursor: 'pointer',
              padding: '0.5rem 1rem',
            }}
          >
            Pricing
          </button>
          {!isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: colors.textPrimary,
                  fontSize: '1rem',
                  fontWeight: '600',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '600',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign Up Free
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/n-back')}
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                border: 'none',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to Game
            </button>
          )}
        </div>
      </nav>
      
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Breadcrumbs */}
        <nav style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '2rem',
          fontSize: '0.9rem',
          color: colors.textSecondary
        }}>
          <Link to="/" style={{ color: colors.textSecondary, textDecoration: 'none' }}>
            Home
          </Link>
          <span>/</span>
          <Link to="/articles" style={{ color: colors.textSecondary, textDecoration: 'none' }}>
            Articles
          </Link>
          <span>/</span>
          <span style={{ color: colors.textPrimary }}>{article.title}</span>
        </nav>

        {/* Back Button */}
        <button
          onClick={() => navigate('/articles')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            borderRadius: '8px',
            color: colors.textSecondary,
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '2rem',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
            e.currentTarget.style.color = colors.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)';
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <ArrowLeft size={16} />
          Back to Articles
        </button>

        {/* Article Header */}
        <article>
          {/* Category & Meta Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {article.category && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'rgba(167, 139, 250, 0.2)',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: '#c4b5fd'
              }}>
                <Tag size={16} />
                {article.category}
              </div>
            )}
            {article.readTime && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: colors.textSecondary
              }}>
                <Clock size={16} />
                {article.readTime}
              </div>
            )}
          </div>

          {/* Title */}
          <h1 style={{
            margin: '0 0 1.5rem 0',
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            fontWeight: '800',
            lineHeight: '1.2',
            color: colors.textPrimary
          }}>
            {article.title}
          </h1>

          {/* Author & Date */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            paddingBottom: '1.5rem',
            borderBottom: '1px solid rgba(100, 116, 139, 0.3)',
            marginBottom: '2rem',
            flexWrap: 'wrap'
          }}>
            {article.author && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.95rem',
                color: colors.textSecondary
              }}>
                <User size={18} />
                {article.author}
              </div>
            )}
            {article.date && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.95rem',
                color: colors.textSecondary
              }}>
                <Calendar size={18} />
                {new Date(article.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            )}
          </div>

          {/* Article Content */}
          <div style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: colors.textPrimary
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({node, ...props}) => (
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '700',
                    marginTop: '2.5rem',
                    marginBottom: '1rem',
                    color: colors.textPrimary,
                    lineHeight: '1.3'
                  }} {...props} />
                ),
                h3: ({node, ...props}) => (
                  <h3 style={{
                    fontSize: '1.4rem',
                    fontWeight: '600',
                    marginTop: '2rem',
                    marginBottom: '0.75rem',
                    color: colors.textPrimary,
                    lineHeight: '1.3'
                  }} {...props} />
                ),
                p: ({node, ...props}) => (
                  <p style={{
                    marginBottom: '1.5rem',
                    lineHeight: '1.8',
                    color: colors.textPrimary
                  }} {...props} />
                ),
                a: ({node, ...props}) => (
                  <a style={{
                    color: colors.primary,
                    textDecoration: 'none',
                    borderBottom: '1px solid rgba(96, 165, 250, 0.3)',
                    transition: 'border-color 0.2s'
                  }} {...props} />
                ),
                ul: ({node, ...props}) => (
                  <ul style={{
                    marginBottom: '1.5rem',
                    paddingLeft: '1.5rem',
                    color: colors.textPrimary
                  }} {...props} />
                ),
                ol: ({node, ...props}) => (
                  <ol style={{
                    marginBottom: '1.5rem',
                    paddingLeft: '1.5rem',
                    color: colors.textPrimary
                  }} {...props} />
                ),
                li: ({node, ...props}) => (
                  <li style={{
                    marginBottom: '0.5rem',
                    lineHeight: '1.7'
                  }} {...props} />
                ),
                blockquote: ({node, ...props}) => (
                  <blockquote style={{
                    borderLeft: '4px solid rgba(96, 165, 250, 0.5)',
                    paddingLeft: '1.5rem',
                    marginLeft: 0,
                    marginBottom: '1.5rem',
                    fontStyle: 'italic',
                    color: colors.textSecondary
                  }} {...props} />
                ),
                code: ({node, inline, ...props}) => (
                  inline 
                    ? <code style={{
                        background: 'rgba(96, 165, 250, 0.15)',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '4px',
                        fontSize: '0.9em',
                        color: colors.primary
                      }} {...props} />
                    : <code style={{
                        display: 'block',
                        background: 'rgba(15, 23, 42, 0.8)',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        overflowX: 'auto',
                        fontSize: '0.9em'
                      }} {...props} />
                ),
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div style={{
              marginTop: '3rem',
              paddingTop: '2rem',
              borderTop: '1px solid rgba(100, 116, 139, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.75rem'
              }}>
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(30, 41, 59, 0.6)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: colors.textSecondary
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* CTA Section */}
        <div style={{
          marginTop: '3rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(167, 139, 250, 0.15) 100%)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '0.75rem'
          }}>
            Ready to Train Your Brain?
          </h3>
          <p style={{
            fontSize: '1rem',
            color: colors.textSecondary,
            marginBottom: '1.5rem'
          }}>
            Start your cognitive training journey with our advanced n-back trainer
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link
              to="/game"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: colors.primary,
                color: colors.textPrimary,
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'transform 0.2s'
              }}
            >
              Start Training Now
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/signup"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(30, 41, 59, 0.6)',
                border: '2px solid rgba(100, 116, 139, 0.3)',
                color: colors.textPrimary,
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'transform 0.2s'
              }}
            >
              Create Free Account
            </Link>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div style={{ marginTop: '4rem' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: colors.textPrimary
            }}>
              Related Articles
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem'
            }}>
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/articles/${related.slug}`}
                  style={{
                    display: 'block',
                    padding: '1.5rem',
                    background: 'rgba(30, 41, 59, 0.6)',
                    border: '1px solid rgba(100, 116, 139, 0.3)',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                  }}
                >
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    color: colors.textPrimary
                  }}>
                    {related.title}
                  </h3>
                  <p style={{
                    fontSize: '0.9rem',
                    color: colors.textSecondary,
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {related.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleDetailPage;
